<template>
  <form @submit.prevent="checkFormValidation" class="signup-form">
    <!-- Company Name -->
    <div class="form-field">
      <label class="form-field__label">Company name</label>
      <div class="form-field__input-wrapper">
        <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M3 21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5 21V7L13 3V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M19 21V11L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 9V9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M9 12V12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M9 15V15.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M9 18V18.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input
          type="text"
          class="form-field__input"
          v-model="payload.name"
          placeholder="Enter company name"
          :disabled="loading"
        />
      </div>
    </div>

    <!-- Industry -->
    <div class="form-field">
      <label class="form-field__label">Industry</label>
      <div class="form-field__select-wrapper">
        <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <select v-model="payload.industry" class="form-field__select" :disabled="loading">
          <option value="" disabled>Select industry</option>
          <option v-for="ind in industries" :key="ind" :value="ind">{{ ind }}</option>
        </select>
        <svg class="form-field__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <!-- Email -->
    <div class="form-field">
      <label class="form-field__label">Company email</label>
      <div class="form-field__input-wrapper" :class="{ 'form-field__input-wrapper--error': emailError }">
        <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <input
          type="email"
          class="form-field__input"
          v-model="payload.email"
          @blur="validateEmail"
          placeholder="Enter company email"
          :disabled="loading"
        />
      </div>
      <p v-if="emailError" class="form-field__error">{{ emailError }}</p>
    </div>

    <!-- Phone -->
    <div class="form-field">
      <label class="form-field__label">Phone number</label>
      <div class="form-field__phone-group">
        <div class="form-field__country-code">
          <select v-model="countryCode" class="form-field__code-select">
            <option value="+234">+234</option>
            <option value="+1">+1</option>
            <option value="+44">+44</option>
            <option value="+91">+91</option>
            <option value="+27">+27</option>
            <option value="+254">+254</option>
            <option value="+233">+233</option>
          </select>
        </div>
        <div class="form-field__input-wrapper form-field__input-wrapper--phone" :class="{ 'form-field__input-wrapper--error': phoneError }">
          <input
            type="tel"
            class="form-field__input form-field__input--phone"
            v-model="telephoneNumber"
            @blur="validatePhone"
            @input="concatPhoneNumber"
            placeholder="Phone number"
            :disabled="loading"
          />
        </div>
      </div>
      <p v-if="phoneError" class="form-field__error">{{ phoneError }}</p>
    </div>

    <!-- Password -->
    <div class="form-field">
      <label class="form-field__label">Password</label>
      <div class="form-field__input-wrapper" :class="{ 'form-field__input-wrapper--error': passwordError }">
        <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
          <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <input
          :type="showPassword ? 'text' : 'password'"
          class="form-field__input"
          v-model="payload.password"
          @blur="validatePassword"
          @input="checkPassword"
          placeholder="Create a password"
          :disabled="loading"
        />
        <button type="button" class="form-field__toggle" @click="showPassword = !showPassword">
          <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <p v-if="passwordError" class="form-field__error">{{ passwordError }}</p>
    </div>

    <!-- Password Requirements -->
    <div class="password-requirements">
      <div class="requirement" :class="{ 'requirement--valid': passwordLength }">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>At least 8 characters</span>
      </div>
      <div class="requirement" :class="{ 'requirement--valid': containsNumber }">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>At least one number</span>
      </div>
      <div class="requirement" :class="{ 'requirement--valid': containsUppercase }">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>At least one uppercase</span>
      </div>
      <div class="requirement" :class="{ 'requirement--valid': containsSpecial }">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>At least one special character</span>
      </div>
    </div>

    <!-- Confirm Password -->
    <div class="form-field">
      <label class="form-field__label">Confirm password</label>
      <div class="form-field__input-wrapper" :class="{ 'form-field__input-wrapper--error': confirmPasswordError }">
        <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
          <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <input
          :type="showConfirmPassword ? 'text' : 'password'"
          class="form-field__input"
          v-model="payload.confirmPassword"
          @blur="validateConfirmPassword"
          placeholder="Confirm your password"
          :disabled="!payload.password || payload.password.length < 8 || loading"
        />
        <button type="button" class="form-field__toggle" @click="showConfirmPassword = !showConfirmPassword" :disabled="!payload.confirmPassword">
          <svg v-if="!showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <p v-if="confirmPasswordError" class="form-field__error">{{ confirmPasswordError }}</p>
    </div>

    <!-- Terms -->
    <div class="terms-text">
      By creating an account, you agree to GetEquity's
      <a href="https://www.getequity.io/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>,
      <a href="https://www.getequity.io/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>, and
      <a href="https://www.getequity.io/investment-disclaimer/" target="_blank" rel="noopener noreferrer">Indemnity</a>.
    </div>

    <!-- Submit Button -->
    <button type="submit" class="submit-button" :disabled="loading">
      <span v-if="!loading">Create account</span>
      <ButtonLoader v-else color="white" size="medium" />
    </button>
  </form>
</template>

<script>
import ButtonLoader from "@/components/notification/ButtonLoader.vue";
import { industries, emailUrl } from "@/utils/helpers";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "DataCollectionForm",
  
  components: {
    ButtonLoader,
  },
  
  emits: ["submitData"],
  
  data() {
    return {
      payload: {
        name: "",
        industry: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        live: process.env.VUE_APP_LIVE_STATUS,
      },
      countryCode: "+234",
      telephoneNumber: "",
      showPassword: false,
      showConfirmPassword: false,
      emailError: "",
      phoneError: "",
      passwordError: "",
      confirmPasswordError: "",
      containsNumber: false,
      containsSpecial: false,
      containsUppercase: false,
      passwordLength: false,
      industries,
    };
  },
  
  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
    ...mapState({
      loading: (state) => state.loadingModule.createLoading,
      success: (state) => state.loadingModule.createSuccess,
    }),
  },
  
  methods: {
    ...mapActions("loadingModule", ["createSuccess"]),
    emailUrl,
    
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.payload.email) {
        this.emailError = "Email is required";
      } else if (!emailRegex.test(this.payload.email)) {
        this.emailError = "Please enter a valid email";
      } else {
        this.emailError = "";
      }
    },
    
    validatePhone() {
      if (!this.telephoneNumber) {
        this.phoneError = "Phone number is required";
      } else if (this.telephoneNumber.length < 7) {
        this.phoneError = "Please enter a valid phone number";
      } else {
        this.phoneError = "";
      }
    },
    
    validatePassword() {
      if (!this.payload.password) {
        this.passwordError = "Password is required";
      } else if (this.payload.password.length < 8) {
        this.passwordError = "Password must be at least 8 characters";
      } else if (!this.containsUppercase || !this.containsNumber || !this.containsSpecial) {
        this.passwordError = "Password does not meet requirements";
      } else {
        this.passwordError = "";
      }
    },
    
    validateConfirmPassword() {
      if (!this.payload.confirmPassword) {
        this.confirmPasswordError = "Please confirm your password";
      } else if (this.payload.confirmPassword !== this.payload.password) {
        this.confirmPasswordError = "Passwords do not match";
      } else {
        this.confirmPasswordError = "";
      }
    },
    
    checkPassword() {
      const password = this.payload.password;
      this.containsNumber = /[0-9]/.test(password);
      this.containsSpecial = /[#?!@$%^&*-]/.test(password);
      this.containsUppercase = /[A-Z]/.test(password);
      this.passwordLength = password.length >= 8;
    },
    
    concatPhoneNumber() {
      this.payload.phone = this.countryCode + this.telephoneNumber;
    },
    
    checkFormValidation() {
      this.validateEmail();
      this.validatePhone();
      this.validatePassword();
      this.validateConfirmPassword();
      
      if (!this.payload.name) {
        return;
      }
      
      if (this.emailError || this.phoneError || this.passwordError || this.confirmPasswordError) {
        return;
      }
      
      this.submitData();
    },
    
    submitData() {
      switch (this.$route.params.type) {
        case "private-raise":
          this.payload.type = "Community";
          break;
        case "public-raise":
          this.payload.type = "Crowdfund Company";
          break;
        case "employee-benefit":
          this.payload.type = "Company";
          break;
        case "api-client":
          this.payload.type = "API_KEY";
          break;
        case "credit-provider":
          this.payload.type = "Credit";
          break;
        case "invest":
          this.payload.type = "Invest";
          break;
        default:
          break;
      }
      const submitPayload = { ...this.payload };
      delete submitPayload.confirmPassword;
      this.$emit("submitData", submitPayload);
    },
    
    prefillForm() {
      if (this.organisationDetails) {
        this.payload.name = this.organisationDetails.name || "";
        this.payload.industry = this.organisationDetails.industry || "";
        this.payload.email = this.organisationDetails.email || "";
        this.payload.phone = this.organisationDetails.phone || "";
      }
    },
  },
  
  mounted() {
    this.prefillForm();
  },
  
  watch: {
    success(newValue) {
      if (newValue === true) {
        this.payload = {
          name: "",
          industry: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
        };
        this.telephoneNumber = "";
        this.createSuccess(false);
      }
    },
    organisationDetails() {
      this.prefillForm();
    },
  },
};
</script>

<style lang="scss" scoped>
.signup-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field {
  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  &__input-wrapper,
  &__select-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:focus-within {
      border-color: #4D84FF;
      background: white;
      box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
    }

    &--error {
      border-color: #EF4444;
      background: #FEF2F2;

      &:focus-within {
        border-color: #EF4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }

      .form-field__icon {
        color: #EF4444;
      }
    }

    &--phone {
      flex: 1;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  &__icon {
    position: absolute;
    left: 14px;
    color: #94A3B8;
    pointer-events: none;
    flex-shrink: 0;
  }

  &__chevron {
    position: absolute;
    right: 14px;
    color: #94A3B8;
    pointer-events: none;
  }

  &__input,
  &__select {
    width: 100%;
    padding: 14px 14px 14px 44px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    background: transparent;
    border: none;
    outline: none;
    border-radius: 10px;

    &::placeholder {
      color: #94A3B8;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--phone {
      padding-left: 14px;
    }
  }

  &__select {
    appearance: none;
    cursor: pointer;
    padding-right: 40px;
  }

  &__phone-group {
    display: flex;
  }

  &__country-code {
    flex-shrink: 0;
  }

  &__code-select {
    height: 100%;
    padding: 14px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-right: none;
    border-radius: 10px 0 0 10px;
    outline: none;
    cursor: pointer;

    &:focus {
      border-color: #4D84FF;
    }
  }

  &__toggle {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #94A3B8;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
      color: #64748B;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__error {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #EF4444;
    margin: 6px 0 0 0;
  }
}

.password-requirements {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #F1F5F9;
  border-radius: 6px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #94A3B8;
  transition: all 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &--valid {
    background: #ECFDF5;
    color: #10B981;
  }
}

.terms-text {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #64748B;
  text-align: center;
  line-height: 1.5;

  a {
    color: #4D84FF;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.submit-button {
  width: 100%;
  background: #4D84FF;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: white;
  padding: 14px 24px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;

  &:hover:not(:disabled) {
    background: #3B6FE8;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(77, 132, 255, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}
</style>
